import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  getDefaultTextModel,
  getTextModelOption,
  getTextModelOptions,
  IMAGE_GENERATION_MODEL,
  type TextModelSelectionId,
} from '@creanizer/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@creanizer/ui'
import { setCurrentGeneratorModel } from '../../../lib/generatorModelSelection'
import { useSettingsStore } from '../../../stores/settingsStore'

interface GeneratorModelContextValue {
  tool: string
  modelId: TextModelSelectionId
  setModelId: (modelId: TextModelSelectionId) => void
}

const GeneratorModelContext = createContext<GeneratorModelContextValue | null>(null)

export function GeneratorModelProvider({
  tool,
  children,
}: {
  tool: string
  children: ReactNode
}) {
  const selectedProvider = useSettingsStore(state => state.selectedProvider)
  const selectedModelId = useSettingsStore(state => state.selectedModelId)
  const setSelectedModel = useSettingsStore(state => state.setSelectedModel)
  const options = getTextModelOptions(tool, selectedProvider)
  const defaultModel = getDefaultTextModel(tool, selectedProvider)
  const preferredModel = options.some(option => option.id === selectedModelId)
    ? selectedModelId
    : defaultModel
  const [modelId, setModelIdState] = useState(preferredModel)

  useEffect(() => {
    const nextModel = preferredModel
    setModelIdState(nextModel)
    setCurrentGeneratorModel(tool, nextModel)
    if (nextModel !== selectedModelId) void setSelectedModel(nextModel)
  }, [tool, preferredModel, selectedModelId, setSelectedModel])

  const setModelId = useCallback((nextModelId: TextModelSelectionId) => {
    setCurrentGeneratorModel(tool, nextModelId)
    setModelIdState(nextModelId)
    void setSelectedModel(nextModelId)
  }, [tool, setSelectedModel])

  const value = useMemo(
    () => ({ tool, modelId, setModelId }),
    [tool, modelId, setModelId]
  )

  return (
    <GeneratorModelContext.Provider value={value}>
      {children}
    </GeneratorModelContext.Provider>
  )
}

export function useGeneratorModel(tool: string): TextModelSelectionId {
  const context = useContext(GeneratorModelContext)
  const selectedProvider = useSettingsStore(state => state.selectedProvider)
  return context?.tool === tool
    ? context.modelId
    : getDefaultTextModel(tool, selectedProvider)
}

export function GeneratorModelSelector({ tool }: { tool: string }) {
  const context = useContext(GeneratorModelContext)
  const selectedProvider = useSettingsStore(state => state.selectedProvider)
  const modelId = context?.modelId ?? getDefaultTextModel(tool, selectedProvider)
  const selected = getTextModelOption(modelId)
  const options = getTextModelOptions(tool, selectedProvider)

  return (
    <div className="card mb-4 p-4">
      <div className="grid gap-2 md:grid-cols-[220px_1fr] md:items-center">
        <div>
          <div className="text-sm font-semibold">
            {tool === 'imagePrompt' ? '使用モデル（プロンプト生成）' : '使用モデル'}
          </div>
          <div className="text-xs text-muted-foreground">
            通常生成とストリーミングで共通です
          </div>
        </div>
        <Select
          value={modelId}
          onValueChange={(value) =>
            context?.setModelId(value as TextModelSelectionId)
          }
        >
          <SelectTrigger aria-label="使用モデルを選択">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {selected.description}
      </p>
      {tool === 'imagePrompt' && (
        <p className="mt-1 text-xs text-muted-foreground">
          画像生成本体は {IMAGE_GENERATION_MODEL} を使用します
        </p>
      )}
    </div>
  )
}
